import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import {
  Send,
  Paperclip,
  Mic,
  User,
  MessageCircle,
  FileText,
  Package,
  Search,
  Phone,
  Mail,
  Bell,
  Trash2,
  ArrowRight,
  Pause,
} from "lucide-react";
import {
  getUserMessages,
  createMessage,
  getAllOrders,
  getCurrentUser,
  isShop,
  getUsersWithMessages,
  updateOrder,
  createOrder,
  deleteOrder,
  pb,
} from "../lib/pocketbase";
import { User as UserType, Message, Order } from "../types";
import Input from "../components/ui/Input";
import Button from "../components/ui/Button";

type UserWithLastMessage = UserType & { lastMessageTime: number };

const ChatPage: React.FC = () => {
  const [users, setUsers] = useState<UserWithLastMessage[]>([]);
  const [selectedUser, setSelectedUser] = useState<UserType | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sending, setSending] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [messageText, setMessageText] = useState("");
  const [messageFile, setMessageFile] = useState<File | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [activeTab, setActiveTab] = useState<"chat" | "orders">("chat");
  const [messageCounts, setMessageCounts] = useState<{
    [userId: string]: number;
  }>({});
  const [newMessageAlert, setNewMessageAlert] = useState<string | null>(null);
  const [notifiedUsers, setNotifiedUsers] = useState<Set<string>>(new Set());
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [savedOrders, setSavedOrders] = useState<Set<string>>(new Set());
  const [updateTimeouts, setUpdateTimeouts] = useState<
    Map<string, NodeJS.Timeout>
  >(new Map());
  const [showNewOrderModal, setShowNewOrderModal] = useState(false);
  const [newOrderForm, setNewOrderForm] = useState({
    title: "",
    weight: "",
    status: "pending" as "pending" | "done" | "delivery",
  });
  const [creatingOrder, setCreatingOrder] = useState(false);
  const [deleteConfirmOrder, setDeleteConfirmOrder] = useState<Order | null>(
    null
  );
  const [deletingOrder, setDeletingOrder] = useState(false);
  const [createOrderMode, setCreateOrderMode] = useState<
    "withMessage" | "orderOnly"
  >("withMessage");
  const [selectedProductImage, setSelectedProductImage] = useState<{
    image: string;
    title: string;
  } | null>(null);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);

  const currentAdmin = getCurrentUser();

  const getUserNameById = (userId: string): string => {
    const user = users.find((u) => u.id === userId);
    if (user) {
      return user.first_name && user.last_name
        ? `${user.first_name} ${user.last_name}`
        : user.first_name || user.last_name || user.email || "کاربر ناشناس";
    }
    return "کاربر ناشناس";
  };

  const formatPersianDateTime = (dateString: string): string => {
    const date = new Date(dateString);

    // Convert to Persian date
    const persianDate = new Intl.DateTimeFormat("fa-IR", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).format(date);

    // Convert Persian numbers to English numbers
    const persianToEnglish = (str: string) => {
      const persianNumbers = ["۰", "۱", "۲", "۳", "۴", "۵", "۶", "۷", "۸", "۹"];
      const englishNumbers = ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9"];

      return str
        .split("")
        .map((char) => {
          const index = persianNumbers.indexOf(char);
          return index !== -1 ? englishNumbers[index] : char;
        })
        .join("");
    };

    return persianToEnglish(persianDate);
  };

  useEffect(() => {
    fetchUsers();

    // Set up real-time subscription for messages
    let unsubscribe: (() => void) | null = null;

    const setupSubscription = async () => {
      unsubscribe = await pb.collection("messages").subscribe("*", (e) => {
        if (e.action === "create") {
          const newMessage = e.record;

          // Handle messages from users (no admin_id)
          if (newMessage.user_id && !newMessage.admin_id) {
            const userId = newMessage.user_id;

            // Set message count to show red bullet
            setMessageCounts((prev) => ({
              ...prev,
              [userId]: 1,
            }));

            // Update user list order based on last message time
            setUsers((prev) => {
              const updatedUsers = prev.map((user) => {
                if (user.id === userId) {
                  return {
                    ...user,
                    lastMessageTime: new Date(newMessage.created).getTime(),
                  };
                }
                return user;
              });

              // Sort by last message time (newest first)
              return updatedUsers.sort(
                (a, b) => b.lastMessageTime - a.lastMessageTime
              );
            });

            // Update messages if this user is currently selected
            if (selectedUser?.id === userId) {
              fetchUserData(userId);
            }
          }

          // Handle messages from admin (has admin_id)
          if (newMessage.user_id && newMessage.admin_id) {
            const userId = newMessage.user_id;

            // Update user list order based on last message time
            setUsers((prev) => {
              const updatedUsers = prev.map((user) => {
                if (user.id === userId) {
                  return {
                    ...user,
                    lastMessageTime: new Date(newMessage.created).getTime(),
                  };
                }
                return user;
              });

              // Sort by last message time (newest first)
              return updatedUsers.sort(
                (a, b) => b.lastMessageTime - a.lastMessageTime
              );
            });

            // Update messages if this user is currently selected
            if (selectedUser?.id === userId) {
              fetchUserData(userId);
            }
          }
        }
      });
    };

    setupSubscription();

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [selectedUser]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Request notification permission on mount
  useEffect(() => {
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission();
    }
  }, []);

  // Play notification sound
  const playNotificationSound = () => {
    try {
      // Create a simple beep sound using Web Audio API
      const audioContext = new (window.AudioContext ||
        (window as any).webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      oscillator.frequency.value = 800; // DING frequency
      oscillator.type = "sine";

      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(
        0.01,
        audioContext.currentTime + 0.3
      );

      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.3);
    } catch (error) {
      console.error("Error playing notification sound:", error);
    }
  };

  // Show browser notification
  const showBrowserNotification = (userName: string) => {
    if ("Notification" in window && Notification.permission === "granted") {
      new Notification("پیام جدید", {
        body: `پیام جدید از ${userName}`,
        icon: "/favicon.ico",
        dir: "rtl",
        lang: "fa",
      });
    }
  };

  // Watch for new messages and trigger notifications
  useEffect(() => {
    Object.keys(messageCounts).forEach((userId) => {
      if (messageCounts[userId] > 0) {
        setNotifiedUsers((prev) => {
          // Only notify if not already notified
          if (!prev.has(userId)) {
            const userName = getUserNameById(userId);

            // Play sound
            playNotificationSound();

            // Show browser notification
            showBrowserNotification(userName);

            // Mark as notified
            return new Set(prev).add(userId);
          }
          return prev;
        });
      }
    });
  }, [messageCounts]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      updateTimeouts.forEach((timeout) => clearTimeout(timeout));
    };
  }, []);

  // Refresh orders when switching to orders tab
  useEffect(() => {
    if (activeTab === "orders" && selectedUser) {
      const refreshOrders = async () => {
        try {
          const ordersData = await getAllOrders();
          setOrders(
            ordersData.filter((order) => order.user_id === selectedUser.id)
          );
        } catch (error) {
          console.error("Error refreshing orders:", error);
        }
      };
      refreshOrders();
    }
  }, [activeTab, selectedUser]);

  const fetchUsers = async () => {
    try {
      const usersData = await getUsersWithMessages();

      // Get last message time for each user and sort by it
      const usersWithLastMessage = await Promise.all(
        usersData.map(async (user) => {
          try {
            const userMessages = await getUserMessages(user.id);
            const lastMessage = userMessages[userMessages.length - 1];
            return {
              ...user,
              lastMessageTime: lastMessage
                ? new Date(lastMessage.created).getTime()
                : 0,
            };
          } catch (error) {
            return {
              ...user,
              lastMessageTime: 0,
            };
          }
        })
      );

      // Sort by last message time (newest first)
      const sortedUsers = usersWithLastMessage.sort(
        (a, b) => b.lastMessageTime - a.lastMessageTime
      );

      setUsers(sortedUsers);
    } catch (error: any) {
      console.error("Error fetching users:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserData = async (userId: string) => {
    setLoadingMessages(true);
    try {
      console.log("Loading messages for user:", userId);
      const [messagesData, ordersData] = await Promise.all([
        getUserMessages(userId),
        getAllOrders(),
      ]);

      console.log("Loaded messages:", messagesData.length);
      setMessages(messagesData);
      setOrders(ordersData.filter((order) => order.user_id === userId));
    } catch (error: any) {
      console.error("Error fetching user data:", error);
    } finally {
      setLoadingMessages(false);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleSendMessage = async () => {
    if (!selectedUser || (!messageText.trim() && !messageFile)) return;

    setSending(true);
    try {
      const formData = new FormData();
      formData.append("user_id", selectedUser.id);
      formData.append("admin_id", currentAdmin?.id || "");
      formData.append(
        "message_type",
        messageFile ? getFileType(messageFile) : "text"
      );

      if (messageText.trim()) {
        formData.append("text", messageText.trim());
      }

      if (messageFile) {
        formData.append("file", messageFile);
      }

      await createMessage(formData);

      setMessageText("");
      setMessageFile(null);

      // Refresh messages
      if (selectedUser) {
        const newMessages = await getUserMessages(selectedUser.id);
        setMessages(newMessages);
      }
    } catch (error: any) {
      console.error("Error sending message:", error);
    } finally {
      setSending(false);
    }
  };

  const getFileType = (file: File): string => {
    if (file.type.startsWith("image/")) return "image";
    if (file.type.startsWith("video/")) return "video";
    if (file.type.startsWith("audio/")) return "audio";
    if (file.type === "application/pdf") return "pdf";
    return "text";
  };

  const handleFileSelect = (file: File) => {
    setMessageFile(file);
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data);
      };

      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, {
          type: "audio/wav",
        });
        const audioFile = new File([audioBlob], "audio-message.wav", {
          type: "audio/wav",
        });
        setMessageFile(audioFile);
        setIsRecording(false);
      };

      mediaRecorder.start();
      setIsRecording(true);
    } catch (error) {
      console.error("Error starting recording:", error);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current.stream
        .getTracks()
        .forEach((track) => track.stop());
    }
  };

  const getMessageIcon = (messageType: string) => {
    switch (messageType) {
      case "image":
        return "🖼️";
      case "video":
        return "🎥";
      case "audio":
        return "🎵";
      case "pdf":
        return "📄";
      case "product":
        return "📦";
      case "confirmPrePayment":
        return "💰";
      case "confirmOfDesign":
        return "✏️";
      case "confirmToMake":
        return "🔨";
      case "requestInvoice":
        return "📋";
      case "finalInvoice":
        return "🧾";
      default:
        return "";
    }
  };

  const getConfirmationStatus = (message: Message) => {
    const confirmTypes = [
      "confirmPrePayment",
      "confirmOfDesign",
      "confirmToMake",
      "requestInvoice",
      "finalInvoice",
    ];

    if (!confirmTypes.includes(message.message_type || "")) {
      return null;
    }
    return message.confirm || "none";
  };

  const getConfirmationLabel = (confirm: string) => {
    switch (confirm) {
      case "yes":
        return "کاربر تایید کرد ✓";
      case "no":
        return "کاربر رد کرد ✗";
      case "none":
        return "در انتظار تایید";
    }
  };

  const renderTextDetail = (
    textDetail: Array<{ label: string; key: string; value: string }>
  ) => {
    if (!textDetail || textDetail.length === 0) return null;

    return (
      <div className="mt-2 p-3 bg-white bg-opacity-10 rounded-lg">
        <div className="space-y-2">
          {textDetail.map((item, index) => (
            <div key={index} className="flex justify-between items-center">
              <span className="text-sm font-medium">{item.label}:</span>
              <span className="text-sm">{item.value}</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const getConfirmationColor = (confirm: string) => {
    switch (confirm) {
      case "yes":
        return "bg-green-500 text-white";
      case "no":
        return "bg-red-500 text-white";
      case "none":
      default:
        return "bg-yellow-500 text-white";
    }
  };

  const getMessageLabel = (messageType: string) => {
    switch (messageType) {
      case "confirmPrePayment":
        return "تایید پیش پرداخت";
      case "confirmOfDesign":
        return "تایید طراحی";
      case "confirmToMake":
        return "تایید ساخت";
      case "requestInvoice":
        return "درخواست فاکتور";
      case "finalInvoice":
        return "فاکتور نهایی";
      case "product":
        return "طرح";
      default:
        return messageType;
    }
  };

  const handleOrderUpdate = async (
    orderId: string,
    field: "title" | "weight" | "status",
    value: string
  ) => {
    try {
      await updateOrder(orderId, { [field]: value });

      // Update the local orders state
      setOrders((prevOrders) =>
        prevOrders.map((order) =>
          order.id === orderId ? { ...order, [field]: value } : order
        )
      );

      // Show success indicator
      setSavedOrders((prev) => new Set(prev).add(orderId));

      // Remove success indicator after 2 seconds
      setTimeout(() => {
        setSavedOrders((prev) => {
          const newSet = new Set(prev);
          newSet.delete(orderId);
          return newSet;
        });
      }, 2000);
    } catch (error) {
      console.error("Error updating order:", error);
    }
  };

  const debouncedOrderUpdate = (
    orderId: string,
    field: "title" | "weight" | "status",
    value: string
  ) => {
    // Clear existing timeout for this order
    const existingTimeout = updateTimeouts.get(orderId);
    if (existingTimeout) {
      clearTimeout(existingTimeout);
    }

    // Update local state immediately for responsive UI
    setOrders((prevOrders) =>
      prevOrders.map((order) =>
        order.id === orderId ? { ...order, [field]: value } : order
      )
    );

    // Set new timeout for API call
    const timeout = setTimeout(() => {
      handleOrderUpdate(orderId, field, value);
      setUpdateTimeouts((prev) => {
        const newMap = new Map(prev);
        newMap.delete(orderId);
        return newMap;
      });
    }, 1000); // Wait 1 second after user stops typing

    setUpdateTimeouts((prev) => new Map(prev).set(orderId, timeout));
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case "pending":
        return "در حال آماده سازی";
      case "done":
        return "آماده شده";
      case "delivery":
        return "تحویل";
      default:
        return status;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      case "done":
        return "bg-green-100 text-green-800";
      case "delivery":
        return "bg-blue-100 text-blue-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const handleCreateOrderWithMessage = async () => {
    if (!selectedUser || !newOrderForm.title.trim()) return;

    setCreatingOrder(true);
    try {
      // const orderData = {
      //   user_id: selectedUser.id,
      //   title: newOrderForm.title.trim(),
      //   weight: newOrderForm.weight.trim(),
      //   status: newOrderForm.status,
      // };

      // const newOrder = await createOrder(orderData);

      // Create confirmation message for the user
      const messageFormData = new FormData();
      messageFormData.append("user_id", selectedUser.id);
      messageFormData.append("admin_id", currentAdmin?.id || "");
      messageFormData.append("message_type", "confirmPrePayment");
      messageFormData.append("text", "لطفا سفارش خود را تایید کنید");
      messageFormData.append(
        "text_detail",
        JSON.stringify([
          {
            label: "عنوان",
            key: "title",
            value: newOrderForm.title.trim(),
          },
          {
            label: "وزن",
            key: "weight",
            value: newOrderForm.weight.trim() || "تعیین نشده",
          },
        ])
      );
      messageFormData.append("confirm", "none");

      await createMessage(messageFormData);

      // Reset form
      setNewOrderForm({
        title: "",
        weight: "",
        status: "pending",
      });
      setShowNewOrderModal(false);

      // Refresh orders list
      if (selectedUser) {
        const ordersData = await getAllOrders();
        setOrders(
          ordersData.filter((order) => order.user_id === selectedUser.id)
        );
      }

      // Refresh messages list
      if (selectedUser) {
        const newMessages = await getUserMessages(selectedUser.id);
        setMessages(newMessages);
      }
    } catch (error) {
      console.error("Error creating order with message:", error);
    } finally {
      setCreatingOrder(false);
    }
  };

  const handleCreateComment = async () => {
    if (!selectedUser) return;

    try {
      const messageFormData = new FormData();
      messageFormData.append("user_id", selectedUser.id);
      messageFormData.append("admin_id", currentAdmin?.id || "");
      messageFormData.append("message_type", "makeComment");
      messageFormData.append("text", "لطفا نظر خود را ثبت کنید");

      await createMessage(messageFormData);

      // Refresh messages list
      if (selectedUser) {
        const newMessages = await getUserMessages(selectedUser.id);
        setMessages(newMessages);
      }
    } catch (error) {
      console.error("Error creating comment message:", error);
    }
  };

  const handleCreateOrderOnly = async () => {
    if (!selectedUser || !newOrderForm.title.trim()) return;

    setCreatingOrder(true);
    try {
      const orderData = {
        user_id: selectedUser.id,
        title: newOrderForm.title.trim(),
        weight: newOrderForm.weight.trim(),
        status: newOrderForm.status,
      };

      await createOrder(orderData);

      // Reset form
      setNewOrderForm({
        title: "",
        weight: "",
        status: "pending",
      });
      setShowNewOrderModal(false);

      // Refresh orders list
      if (selectedUser) {
        const ordersData = await getAllOrders();
        setOrders(
          ordersData.filter((order) => order.user_id === selectedUser.id)
        );
      }
    } catch (error) {
      console.error("Error creating order:", error);
    } finally {
      setCreatingOrder(false);
    }
  };

  const handleDeleteOrder = async (orderId: string) => {
    setDeletingOrder(true);
    try {
      await deleteOrder(orderId);

      // Remove order from local state
      setOrders((prevOrders) =>
        prevOrders.filter((order) => order.id !== orderId)
      );

      // Close confirmation modal
      setDeleteConfirmOrder(null);
    } catch (error) {
      console.error("Error deleting order:", error);
    } finally {
      setDeletingOrder(false);
    }
  };

  const handleUserSelect = async (user: UserType) => {
    setSelectedUser(user);
    // Reset message count for selected user
    setMessageCounts((prev) => ({
      ...prev,
      [user.id]: 0,
    }));
    // Clear notification for selected user so they can be notified again
    setNotifiedUsers((prev) => {
      const newSet = new Set(prev);
      newSet.delete(user.id);
      return newSet;
    });

    // Load all messages for the selected user
    await fetchUserData(user.id);
  };

  const filteredUsers = users.filter(
    (user) =>
      user.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="h-[100dvh] flex bg-gray-100 overflow-hidden">
      {/* New Message Alert */}
      {newMessageAlert && (
        <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50 bg-green-500 text-white px-4 sm:px-6 py-3 rounded-lg shadow-lg flex items-center space-x-2 space-x-reverse max-w-[90vw]">
          <Bell className="h-5 w-5 shrink-0" />
          <span className="truncate">{newMessageAlert}</span>
          <button
            onClick={() => setNewMessageAlert(null)}
            className="mr-2 hover:bg-green-600 rounded p-1 shrink-0"
          >
            ✕
          </button>
        </div>
      )}

      {/* Sidebar - Users List */}
      <div
        className={`${
          selectedUser ? "hidden md:flex" : "flex"
        } w-full md:w-80 md:shrink-0 bg-white border-l border-gray-200 flex-col`}
      >
        {/* Header */}
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center justify-between gap-2">
            <h1 className="text-lg font-semibold text-gray-900">
              {isShop() ? "چت فروشگاه" : "چت با کاربران"}
            </h1>
            <Link
              to="/"
              className="text-sm text-primary-600 hover:text-primary-700 shrink-0"
            >
              {isShop() ? "داشبورد" : "پنل"}
            </Link>
          </div>
          <div className="mt-3 relative">
            <input
              type="text"
              placeholder="جستجو در کاربران..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          </div>
        </div>

        {/* Users List */}
        <div className="flex-1 overflow-y-auto">
          {filteredUsers.map((user) => (
            <div
              key={user.id}
              onClick={() => handleUserSelect(user)}
              className={`p-4 border-b border-gray-100 cursor-pointer hover:bg-gray-50 transition-colors ${
                selectedUser?.id === user.id
                  ? "bg-primary-50 border-primary-200"
                  : ""
              }`}
            >
              <div className="flex items-center min-w-0">
                <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center shrink-0">
                  {user.avatar ? (
                    <img
                      src={`${process.env.REACT_APP_POCKETBASE_URL}/api/files/users/${user.id}/${user.avatar}`}
                      alt={`${user.first_name} ${user.last_name}`}
                      className="w-12 h-12 rounded-full object-cover"
                    />
                  ) : (
                    <User className="h-6 w-6 text-primary-600" />
                  )}
                </div>
                <div className="mr-3 flex-1 min-w-0">
                  <p className="font-medium text-gray-900 truncate">
                    {user.first_name && user.last_name
                      ? `${user.first_name} ${user.last_name}`
                      : user.first_name || user.last_name || "بدون نام"}
                  </p>
                  <p className="text-sm text-gray-500 truncate">{user.email}</p>
                </div>
                <div className="flex items-center space-x-2 space-x-reverse shrink-0">
                  <MessageCircle className="h-4 w-4 text-gray-400" />
                  {messageCounts[user.id] > 0 && (
                    <div className="bg-red-500 rounded-full w-2 h-2"></div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Main Chat Area */}
      <div
        className={`${
          selectedUser ? "flex" : "hidden md:flex"
        } flex-1 flex-col min-w-0`}
      >
        {selectedUser ? (
          <>
            {/* Chat Header */}
            <div className="bg-white border-b border-gray-200 p-3 sm:p-4">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center min-w-0">
                  <button
                    type="button"
                    onClick={() => setSelectedUser(null)}
                    className="md:hidden p-2 -mr-1 ml-1 text-gray-600 hover:text-gray-900 rounded-lg shrink-0"
                    aria-label="بازگشت به لیست"
                  >
                    <ArrowRight className="h-5 w-5" />
                  </button>
                  <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center shrink-0">
                    {selectedUser.avatar ? (
                      <img
                        src={`${process.env.REACT_APP_POCKETBASE_URL}/api/files/users/${selectedUser.id}/${selectedUser.avatar}`}
                        alt={`${selectedUser.first_name} ${selectedUser.last_name}`}
                        className="w-10 h-10 rounded-full object-cover"
                      />
                    ) : (
                      <User className="h-5 w-5 text-primary-600" />
                    )}
                  </div>
                  <div className="mr-3 min-w-0">
                    <p className="font-medium text-gray-900 truncate">
                      {selectedUser.first_name && selectedUser.last_name
                        ? `${selectedUser.first_name} ${selectedUser.last_name}`
                        : selectedUser.first_name ||
                          selectedUser.last_name ||
                          "بدون نام"}
                    </p>
                    <p className="text-sm text-gray-500 truncate">
                      {selectedUser.phone || selectedUser.email}
                    </p>
                  </div>
                </div>

                {/* User Info - desktop only */}
                <div className="hidden lg:flex items-center space-x-2 space-x-reverse shrink-0">
                  {selectedUser.phone && (
                    <div className="flex items-center text-sm text-gray-500">
                      <Phone className="h-4 w-4 ml-1" />
                      {selectedUser.phone}
                    </div>
                  )}
                  <div className="flex items-center text-sm text-gray-500">
                    <Mail className="h-4 w-4 ml-1" />
                    {selectedUser.email}
                  </div>
                </div>
              </div>
            </div>

            {/* Tabs */}
            <div className="bg-white border-b border-gray-200">
              <div className="flex justify-between items-center">
                <div className="flex">
                  <button
                    onClick={() => setActiveTab("chat")}
                    className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                      activeTab === "chat"
                        ? "border-primary-500 text-primary-600"
                        : "border-transparent text-gray-500 hover:text-gray-700"
                    }`}
                  >
                    چت
                  </button>
                  <button
                    onClick={() => setActiveTab("orders")}
                    className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                      activeTab === "orders"
                        ? "border-primary-500 text-primary-600"
                        : "border-transparent text-gray-500 hover:text-gray-700"
                    }`}
                  >
                    سفارشات ({orders.length})
                  </button>
                </div>
              </div>
            </div>

            {/* Content Area */}
            <div className="flex-1 overflow-hidden">
              {activeTab === "chat" && (
                <div className="h-full flex flex-col">
                  {/* Messages */}
                  <div className="flex-1 overflow-y-auto p-4 space-y-4">
                    {loadingMessages && (
                      <div className="flex justify-center py-4">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
                        <span className="mr-2 text-sm text-gray-500">
                          در حال بارگذاری پیام‌ها...
                        </span>
                      </div>
                    )}
                    {messages.map((message) => (
                      <div
                        key={message.id}
                        className={`flex ${
                          message.admin_id ? "justify-end" : "justify-start"
                        }`}
                      >
                        {/* 
                          Message direction logic:
                          - message.admin_id exists → Message from admin to user (right side, blue)
                          - message.admin_id doesn't exist → Message from user to admin (left side, gray)
                        */}
                        <div
                          className={`max-w-[85%] sm:max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                            message.admin_id
                              ? "bg-primary-500 text-white"
                              : "bg-gray-200 text-gray-900"
                          }`}
                        >
                          <div className="flex items-center mb-1">
                            <span className="text-sm mr-2">
                              {getMessageIcon(message.message_type || "text")}
                            </span>
                            {(() => {
                              const confirmStatus =
                                getConfirmationStatus(message);
                              if (confirmStatus) {
                                return (
                                  <span
                                    className={`text-xs px-2 py-1 rounded ${getConfirmationColor(
                                      confirmStatus
                                    )}`}
                                  >
                                    {getConfirmationLabel(confirmStatus)}
                                  </span>
                                );
                              } else if (
                                message.message_type &&
                                [
                                  "confirmPrePayment",
                                  "confirmOfDesign",
                                  "confirmToMake",
                                  "requestInvoice",
                                  "finalInvoice",
                                  "product",
                                ].includes(message.message_type)
                              ) {
                                return (
                                  <span className="text-xs bg-white bg-opacity-20 px-2 py-1 rounded">
                                    {getMessageLabel(message.message_type)}
                                  </span>
                                );
                              }
                              return null;
                            })()}
                          </div>

                          {message.text && (
                            <p className="text-sm">{message.text}</p>
                          )}

                          {/* Show text_detail for confirmation message types */}
                          {message.text_detail &&
                            message.message_type &&
                            [
                              "confirmPrePayment",
                              "confirmOfDesign",
                              "confirmToMake",
                              "requestInvoice",
                              "finalInvoice",
                              "product",
                            ].includes(message.message_type) &&
                            renderTextDetail(message.text_detail)}

                          {message.message_type === "product" &&
                            message.expand?.product_id && (
                              <div className="mt-2 p-3 bg-white bg-opacity-10 rounded-lg">
                                <div className="flex items-center space-x-3 space-x-reverse">
                                  {message.expand.product_id.avatar ? (
                                    <img
                                      src={`${process.env.REACT_APP_POCKETBASE_URL}/api/files/products/${message.expand.product_id.id}/${message.expand.product_id.avatar}`}
                                      alt={message.expand.product_id.title}
                                      className="w-12 h-12 rounded-lg object-cover cursor-pointer hover:opacity-80 transition-opacity"
                                      onClick={() =>
                                        setSelectedProductImage({
                                          image: `${process.env.REACT_APP_POCKETBASE_URL}/api/files/products/${message.expand?.product_id?.id}/${message.expand?.product_id?.avatar}`,
                                          title:
                                            message.expand?.product_id?.title ||
                                            "",
                                        })
                                      }
                                    />
                                  ) : (
                                    <div className="w-12 h-12 bg-gray-300 rounded-lg flex items-center justify-center">
                                      <Package className="h-6 w-6 text-gray-500" />
                                    </div>
                                  )}
                                  <div className="flex-1">
                                    <h4 className="font-medium text-sm">
                                      {message.expand.product_id.title}
                                    </h4>
                                  </div>
                                </div>
                              </div>
                            )}

                          {message.file && (
                            <div className="mt-2">
                              {message.message_type === "image" && (
                                <img
                                  src={`${process.env.REACT_APP_POCKETBASE_URL}/api/files/messages/${message.id}/${message.file}`}
                                  alt="تصویر"
                                  className="max-w-full rounded"
                                />
                              )}
                              {message.message_type === "audio" && (
                                <audio
                                  controls
                                  className="w-full max-w-full h-10 bg-gray-100 rounded"
                                >
                                  <source
                                    src={`${process.env.REACT_APP_POCKETBASE_URL}/api/files/messages/${message.id}/${message.file}`}
                                  />
                                </audio>
                              )}
                              {message.message_type === "video" && (
                                <video controls className="max-w-full rounded">
                                  <source
                                    src={`${process.env.REACT_APP_POCKETBASE_URL}/api/files/messages/${message.id}/${message.file}`}
                                  />
                                </video>
                              )}
                              {message.message_type === "pdf" && (
                                <div className="flex items-center space-x-2 space-x-reverse">
                                  <FileText className="h-4 w-4" />
                                  <a
                                    href={`${process.env.REACT_APP_POCKETBASE_URL}/api/files/messages/${message.id}/${message.file}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-sm underline"
                                  >
                                    دانلود فایل
                                  </a>
                                </div>
                              )}
                            </div>
                          )}

                          <div className="flex items-center justify-between mt-2">
                            <p className="text-xs opacity-70 font-persian">
                              {formatPersianDateTime(message.created)}
                            </p>
                            {message.is_seen && message.admin_id && (
                              <span className="text-xs text-blue-500">✓</span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                    <div ref={messagesEndRef} />
                  </div>

                  {/* Message Input */}
                  <div className="p-3 sm:p-4 bg-white border-t border-gray-200">
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <button
                        onClick={() => fileInputRef.current?.click()}
                        className="p-2 text-gray-500 hover:text-gray-700"
                        type="button"
                      >
                        <Paperclip className="h-5 w-5" />
                      </button>

                      <button
                        onClick={isRecording ? stopRecording : startRecording}
                        type="button"
                        className={`p-2 ${
                          isRecording
                            ? "text-red-500"
                            : "text-gray-500 hover:text-gray-700"
                        }`}
                      >
                        {isRecording ? (
                          <Pause className="h-5 w-5" />
                        ) : (
                          <Mic className="h-5 w-5" />
                        )}
                      </button>

                      <Button
                        onClick={() => {
                          setCreateOrderMode("withMessage");
                          setShowNewOrderModal(true);
                        }}
                        disabled={!selectedUser}
                        className="w-fit text-xs sm:text-sm"
                        size="sm"
                      >
                        ایجاد سفارش
                      </Button>

                      <Button
                        onClick={handleCreateComment}
                        disabled={!selectedUser}
                        className="w-fit text-xs sm:text-sm"
                        size="sm"
                      >
                        ایجاد نظر
                      </Button>
                    </div>

                    <div className="flex items-center gap-2">
                      <div className="flex-1 min-w-0">
                        <Input
                          type="text"
                          placeholder="پیام خود را بنویسید..."
                          value={messageText}
                          onChange={(e) => setMessageText(e.target.value)}
                          onKeyPress={(e) =>
                            e.key === "Enter" && handleSendMessage()
                          }
                        />
                      </div>

                      <Button
                        onClick={handleSendMessage}
                        disabled={
                          sending || (!messageText.trim() && !messageFile)
                        }
                        size="sm"
                        className="shrink-0"
                      >
                        <Send className="h-4 w-4" />
                      </Button>
                    </div>

                    {messageFile && (
                      <div className="mt-2 p-2 bg-gray-50 rounded-lg">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">
                            {messageFile.name}
                          </span>
                          <button
                            onClick={() => setMessageFile(null)}
                            className="text-red-500 hover:text-red-700"
                          >
                            ✕
                          </button>
                        </div>
                      </div>
                    )}

                    <input
                      ref={fileInputRef}
                      type="file"
                      className="hidden"
                      onChange={(e) =>
                        e.target.files?.[0] &&
                        handleFileSelect(e.target.files[0])
                      }
                      accept="image/*,video/*,audio/*,.pdf"
                    />
                  </div>
                </div>
              )}

              {activeTab === "orders" && (
                <div className="h-full overflow-y-auto p-4">
                  {/* Create New Order Button */}
                  <div className="mb-4">
                    <Button
                      onClick={() => {
                        setCreateOrderMode("orderOnly");
                        setShowNewOrderModal(true);
                      }}
                      disabled={!selectedUser}
                      className="w-fit"
                    >
                      ایجاد سفارش جدید
                    </Button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {orders.map((order) => (
                      <div
                        key={order.id}
                        className={`bg-white p-4 rounded-lg border transition-colors duration-200 ${
                          savedOrders.has(order.id)
                            ? "border-green-500 bg-green-50"
                            : "border-gray-200"
                        }`}
                      >
                        <div className="space-y-3">
                          {/* Title */}
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              عنوان سفارش
                            </label>
                            <input
                              type="text"
                              value={order.title || ""}
                              onChange={(e) =>
                                debouncedOrderUpdate(
                                  order.id,
                                  "title",
                                  e.target.value
                                )
                              }
                              placeholder="عنوان سفارش"
                              className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                            />
                          </div>

                          {/* Weight */}
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              وزن (گرم)
                            </label>
                            <input
                              type="text"
                              value={order.weight || ""}
                              onChange={(e) =>
                                debouncedOrderUpdate(
                                  order.id,
                                  "weight",
                                  e.target.value
                                )
                              }
                              placeholder="وزن"
                              className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                            />
                          </div>

                          {/* Status */}
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              وضعیت
                            </label>
                            <select
                              value={order.status || "pending"}
                              onChange={(e) =>
                                handleOrderUpdate(
                                  order.id,
                                  "status",
                                  e.target.value as
                                    | "pending"
                                    | "done"
                                    | "delivery"
                                )
                              }
                              className={`w-full px-2 py-1 text-xs rounded border-0 focus:ring-1 focus:ring-primary-500 ${getStatusColor(
                                order.status || "pending"
                              )}`}
                            >
                              <option value="pending">در حال آماده سازی</option>
                              <option value="done">آماده شده</option>
                              <option value="delivery">تحویل</option>
                            </select>
                          </div>
                        </div>

                        <div className="mt-3 text-xs text-gray-500">
                          {formatPersianDateTime(order.created)}
                        </div>

                        {/* Delete Button */}
                        <div className="mt-3 flex justify-end">
                          <button
                            onClick={() => setDeleteConfirmOrder(order)}
                            className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                            title="حذف سفارش"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>

                  {orders.length === 0 && (
                    <div className="text-center py-8">
                      <Package className="mx-auto h-12 w-12 text-gray-400" />
                      <h3 className="mt-2 text-sm font-medium text-gray-900">
                        هیچ سفارشی یافت نشد
                      </h3>
                      <p className="mt-1 text-sm text-gray-500">
                        این کاربر هنوز سفارشی ندارد
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <MessageCircle className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">
                کاربری را انتخاب کنید
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                برای شروع چت، یک کاربر از لیست انتخاب کنید
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Create New Order Modal */}
      {showNewOrderModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {createOrderMode === "withMessage"
                ? "ایجاد سفارش و ارسال پیام تایید"
                : "ایجاد سفارش جدید"}
            </h3>

            <div className="space-y-4">
              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  عنوان سفارش *
                </label>
                <input
                  type="text"
                  value={newOrderForm.title}
                  onChange={(e) =>
                    setNewOrderForm((prev) => ({
                      ...prev,
                      title: e.target.value,
                    }))
                  }
                  placeholder="عنوان سفارش را وارد کنید"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              {/* Weight */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  وزن (گرم)
                </label>
                <input
                  type="text"
                  value={newOrderForm.weight}
                  onChange={(e) =>
                    setNewOrderForm((prev) => ({
                      ...prev,
                      weight: e.target.value,
                    }))
                  }
                  placeholder="وزن را وارد کنید"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              {/* Status */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  وضعیت سفارش
                </label>
                <select
                  value={newOrderForm.status}
                  onChange={(e) =>
                    setNewOrderForm((prev) => ({
                      ...prev,
                      status: e.target.value as "pending" | "done" | "delivery",
                    }))
                  }
                  className={`w-full px-3 py-2 text-sm rounded-lg border-0 focus:ring-2 focus:ring-primary-500 ${getStatusColor(
                    newOrderForm.status
                  )}`}
                >
                  <option value="pending">در حال آماده سازی</option>
                  <option value="done">آماده شده</option>
                  <option value="delivery">تحویل</option>
                </select>
              </div>
            </div>

            <div className="flex justify-end space-x-3 space-x-reverse mt-6">
              <Button
                onClick={() => setShowNewOrderModal(false)}
                variant="outline"
                disabled={creatingOrder}
              >
                انصراف
              </Button>
              <Button
                onClick={
                  createOrderMode === "withMessage"
                    ? handleCreateOrderWithMessage
                    : handleCreateOrderOnly
                }
                disabled={creatingOrder || !newOrderForm.title.trim()}
              >
                {creatingOrder
                  ? "در حال ایجاد..."
                  : createOrderMode === "withMessage"
                  ? "ایجاد سفارش و ارسال پیام"
                  : "ایجاد سفارش"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Order Confirmation Modal */}
      {deleteConfirmOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              حذف سفارش
            </h3>

            <div className="mb-4">
              <p className="text-gray-600 mb-2">
                آیا مطمئن هستید که می‌خواهید این سفارش را حذف کنید؟
              </p>
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="font-medium text-gray-900">
                  {deleteConfirmOrder.title || "سفارش بدون عنوان"}
                </p>
                {deleteConfirmOrder.weight && (
                  <p className="text-sm text-gray-600 mt-1">
                    وزن: {deleteConfirmOrder.weight}
                  </p>
                )}
              </div>
            </div>

            <div className="flex justify-end space-x-3 space-x-reverse">
              <Button
                onClick={() => setDeleteConfirmOrder(null)}
                variant="outline"
                disabled={deletingOrder}
              >
                انصراف
              </Button>
              <Button
                onClick={() => handleDeleteOrder(deleteConfirmOrder.id)}
                disabled={deletingOrder}
                className="bg-red-500 hover:bg-red-600 text-white"
              >
                {deletingOrder ? "در حال حذف..." : "حذف سفارش"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Product Image Modal */}
      {selectedProductImage && (
        <div
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50"
          onClick={() => setSelectedProductImage(null)}
        >
          <div
            className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex flex-col items-center">
              <img
                src={selectedProductImage.image}
                alt={selectedProductImage.title}
                className="max-w-full h-auto rounded-lg mb-4"
              />
              <h3 className="text-lg font-semibold text-gray-900 text-center">
                {selectedProductImage.title}
              </h3>
              <button
                onClick={() => setSelectedProductImage(null)}
                className="mt-4 px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg text-gray-700 transition-colors"
              >
                بستن
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatPage;
